<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';
import { useToast } from 'vue-toastification';

import { isString, isFetchError } from '~~core';

import type { UserMediaErrors } from '../../../../constants/media';
import { ImageAspectRatio } from '../../../../constants/img';
import { useSubSteps } from '../../../../composables/use-sub-steps';
import AddressProofExample from '../../../../assets/svg/address-proof/address-proof-example.svg?component';
import CameraRecorderModal, { CapturedImage } from '../../../../components/CameraRecorderModal/CameraRecorderModal.vue';
import CameraPhotoPreview from '../../../../components/CameraPhotoPreview/CameraPhotoPreview.vue';
import RequirementPrepareFrame from '../../../../components/RequirementPrepareFrame/RequirementPrepareFrame.vue';
import CameraErrorAlert from '../../../../components/CameraErrorAlert/CameraErrorAlert.vue';
import { useRequirementStep } from '../../../../composables/use-requirement-step';
import { requirementQueryKeys, useFetchRequirement } from '../../../../repositories/requirement';
import { saveRequirement } from '../../../../services/requirement';
import { useRequirement } from '../../../../composables/use-requirement';

const enum SubStep {
  BeforePicture = 'before-picture',
  TakePicture = 'take-picture',
  AfterPicture = 'after-picture',
}

type Photo = {
  blobOrUrl: string | Blob;
  meta?: {
    width: number;
    height: number;
  };
};

const STEP_NAME = 'address_proof';

const { finishRequirement, signatureRequestKey, setActiveRequirement } = useRequirementStep();

setActiveRequirement(STEP_NAME);

const { t } = useI18n();
const toast = useToast();
const queryClient = useQueryClient();
const { requirement, isLoading } = useFetchRequirement(STEP_NAME, signatureRequestKey);
const { evidence, isFilled: isReadonly } = useRequirement(requirement);

const photo = shallowRef<Photo>();
const cameraError = shallowRef<UserMediaErrors>();
const isSaving = shallowRef(false);

const normalSubSteps = [SubStep.BeforePicture, SubStep.TakePicture, SubStep.AfterPicture] as const;
const readonlySubSteps = [SubStep.AfterPicture] as const;
const subSteps = computed(() => (isReadonly.value ? readonlySubSteps : normalSubSteps));

const { nextSubStep, setSubStep, previousSubStep, isSubStep } = useSubSteps(subSteps);

function advanceSubStep() {
  cameraError.value = undefined;
  nextSubStep();
}

function handleCapturePhoto(image: CapturedImage) {
  photo.value = {
    blobOrUrl: image.blob,
    meta: image.meta,
  };

  advanceSubStep();
}

function handleCameraError(error: UserMediaErrors) {
  cameraError.value = error;
}

async function completeRequirement() {
  // Cast blobOrUrl type to blob
  if (isReadonly.value || isString(photo.value?.blobOrUrl)) return finishRequirement();
  if (!photo.value?.blobOrUrl) throw new Error('Missing picture');

  isSaving.value = true;

  try {
    await saveRequirement(STEP_NAME, signatureRequestKey.value, {
      image: photo.value.blobOrUrl,
    });

    await queryClient.invalidateQueries(requirementQueryKeys.show(signatureRequestKey.value, STEP_NAME));
    finishRequirement();
  } catch (error) {
    isSaving.value = false;

    toast.error(t('requirement.officialDoc.errors.save'));

    if (!isFetchError(error)) throw error;
  }
}

watch(evidence, () => {
  if (!evidence.value?.artefact) return;

  photo.value = { blobOrUrl: evidence.value.artefact.imageUrl };
}, { immediate: true });
</script>

<template>
  <div :class="$style.root">
    <CameraErrorAlert
      v-if="cameraError"
      :error-type="cameraError"
    />

    <RequirementPrepareFrame
      v-if="isSubStep(SubStep.BeforePicture) || isSubStep(SubStep.TakePicture)"
      :title="$t('requirement.addressProof.title')"
      :action-text="$t('requirement.common.takePhoto')"
      :loading="isLoading"
      reverse-content
      @action="advanceSubStep()"
    >
      <template #description>
        {{ $t('requirement.addressProof.certifyAddressIsLegible') }}

        <br>

        {{ $t('requirement.addressProof.willAttachToSignedDocument') }}
      </template>
      <template #default>
        <AddressProofExample width="320" />
      </template>
    </RequirementPrepareFrame>

    <CameraRecorderModal
      v-if="isSubStep(SubStep.TakePicture)"
      :aspect-ratio="ImageAspectRatio.RECTANGLE"
      @capture="handleCapturePhoto"
      @camera-error="handleCameraError"
      @close="previousSubStep()"
    />

    <CameraPhotoPreview
      v-if="isSubStep(SubStep.AfterPicture) && photo"
      :picture="photo.blobOrUrl"
      :meta="photo.meta"
      :saving="isSaving"
      :readonly="isReadonly"
      :disable-go-back="isReadonly"
      unknown-size
      @advance="completeRequirement()"
      @go-back="setSubStep(SubStep.BeforePicture)"
    >
      <b>{{ $t('requirement.addressProof.picturePreview.title') }}</b>
    </CameraPhotoPreview>
  </div>
</template>

<style module lang="scss">
.root {
  width: 100%;
  max-width: 496px;
  margin: 0 auto;
  margin-bottom: var(--space-regular);
}
</style>
